import { useState, useEffect, ReactNode, ReactElement, cloneElement } from 'react';
import styled from 'styled-components';
import { colors } from 'ui/theme/colors';
import { half } from 'ui/theme/spacing';
import { CollapsibleContent } from 'ui/collapsibleContent';
import AccordionLabelDeprecated from './AccordionLabelDeprecated';
import AccordionBodyDeprecated from './AccordionBodyDeprecated';

const AccordionContainer = styled.div`
  background-color: ${colors.grey1};
  border-radius: ${half};
`;

type Props = {
  /** Content displayed within the accordian if it is open */
  children: ReactNode;
  /** Whether the component is disabled and can't be toggled */
  isDisabled?: boolean;
  /** Unique identifier for the accordion */
  id?: string;
  /**
   * Whether the Accordion is expanded or not.
   *
   * When this is set, the opening and closing of the accordion must be managed outside the component.
   * If `isOpen` is not present, the open/close state of the component is managed internally.
   */
  isOpen?: boolean;
  /** Callback function ran on open */
  onToggle?: (isOpen: boolean) => void;
};

/**
 * @deprecated
 * this accordion implementation has been deprecated. please use ui/Accordion
 * */
const AccordionDeprecated = ({
  children,
  isDisabled = false,
  isOpen,
  id = 'Accordion',
  onToggle = () => null,
  ...props
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(!!isOpen);

  useEffect(() => {
    setIsExpanded(!!isOpen);
  }, [isOpen]);

  const onClick = () => {
    if (!isDisabled) {
      if (typeof isOpen !== 'boolean') {
        setIsExpanded(!isExpanded);
      }
      onToggle(!isExpanded);
    }
  };

  const labelId = `${id}-AccordionLabel`;
  const bodyId = `${id} -AccordionBody`;

  return (
    <AccordionContainer data-test="accordion-container" id={id} {...props}>
      {(children as ReactElement[]).map((node) => {
        if (
          node.type === AccordionLabelDeprecated ||
          node.props.mdxType === 'AccordionLabelDeprecated'
        ) {
          return cloneElement(node, {
            onClick,
            key: labelId,
            id: labelId,
            isExpanded,
            ariaExpanded: isExpanded,
            ariaDisabled: isDisabled,
            ariaControls: bodyId,
            ...node.props,
          });
        } else if (
          node.type === AccordionBodyDeprecated ||
          node.props.mdxType === 'AccordionBodyDeprecated'
        ) {
          return (
            <CollapsibleContent
              key={bodyId}
              data-test={`${id}-AccordionBody-${isExpanded ? 'Expanded' : 'Collapsed'}`}
              isCollapsed={!isExpanded}
            >
              {cloneElement(node, {
                id: bodyId,
                'aria-labelledby': labelId,
                ...node.props,
              })}
            </CollapsibleContent>
          );
        }
        return null;
      })}
    </AccordionContainer>
  );
};

export default AccordionDeprecated;
